import type { OutgoingBatch, OutgoingEvent, SdkConfig, TrackingLevel } from './types';
import { log } from './log';
import type { Identity } from './identity';
import type { Consent } from './consent';
import { CONFIG_HEADER_NAME, parseConfigHeader, readCachedConfig, storeConfig } from './remote-config';

// Approx serialized size of one event. Used to flush when the accumulated
// payload would exceed config.batch.maxBytes — useful for heatmap-grade
// volumes where event count is low but mouse_trail payloads are large.
function eventSize(event: OutgoingEvent): number {
	try {
		return JSON.stringify(event).length;
	} catch {
		return 0;
	}
}

function devicePixelRatio(): number | null {
	if (typeof window === 'undefined') return null;
	const dpr = window.devicePixelRatio;
	return typeof dpr === 'number' && Number.isFinite(dpr) && dpr > 0 ? dpr : null;
}

/**
 * Buffer + transport.
 *
 * Behavior matches spec §7-8:
 *   - Accumulates events until maxEvents OR maxWaitMs since the first event.
 *   - On flush, POSTs `OutgoingBatch` to the configured endpoint using fetch
 *     (or sendBeacon on page unload).
 *   - 5xx + network errors push events back into the buffer (best-effort
 *     in-memory retry). 4xx is final.
 *   - When consent isn't granted yet, events go to a `pending` list and are
 *     drained as soon as consent flips to allowed.
 *
 * Out of scope for POC: localStorage persistence (spec §8.2). The current
 * in-memory retry survives navigation only via the `pagehide` sendBeacon path.
 */
export class Queue {
	private buffer: OutgoingEvent[] = [];
	private bufferBytes = 0;
	private pending: OutgoingEvent[] = [];
	private timer: ReturnType<typeof setTimeout> | null = null;

	/**
	 * @param onConfig invoked when a response reports a tracking level, so the
	 *   entry can apply it in memory. Optional — the Queue works without it and
	 *   the cache write below is the part that actually matters.
	 */
	constructor(
		private config: SdkConfig,
		private identity: Identity,
		private consent: Consent,
		private onConfig?: (level: TrackingLevel) => void,
	) {
		this.consent.onChange(() => this.drainPending());
		if (typeof window !== 'undefined') {
			window.addEventListener('pagehide', () => this.flush(true));
			window.addEventListener('online', () => this.flush());
		}
	}

	enqueue(event: OutgoingEvent): void {
		if (!this.consent.canTrack()) {
			this.pending.push(event);
			log('queue: held pending consent', event.name);
			return;
		}
		this.buffer.push(event);
		this.bufferBytes += eventSize(event);
		log('queue: enqueued', event.name, `(${this.buffer.length}/${this.config.batch.maxEvents}, ${this.bufferBytes}B)`);
		const { maxEvents, maxBytes } = this.config.batch;
		if (this.buffer.length >= maxEvents || (maxBytes > 0 && this.bufferBytes >= maxBytes)) {
			this.flush();
		} else if (!this.timer) {
			this.timer = setTimeout(() => this.flush(), this.config.batch.maxWaitMs);
		}
	}

	private drainPending(): void {
		if (this.pending.length === 0) return;
		if (!this.consent.canTrack()) return;
		log('queue: draining', this.pending.length, 'pending events');
		const drained = this.pending;
		this.pending = [];
		for (const ev of drained) this.enqueue(ev);
	}

	flush(useBeacon = false): Promise<void> {
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
		}
		if (this.buffer.length === 0) return Promise.resolve();

		// A 403 earlier in this tab means the write key is unknown or inactive, so
		// there is nothing to send to. Derived from the cache on every flush rather
		// than latched into a field: readCachedConfig stops returning the entry once
		// the negative TTL lapses, which makes the retry happen by itself with no
		// timer of our own to get wrong.
		const cached = readCachedConfig(this.config.writeKey);
		if (cached && !cached.active) {
			log('queue: write key rejected earlier, dropping', this.buffer.length, 'events');
			this.buffer = [];
			this.bufferBytes = 0;
			return Promise.resolve();
		}

		const events = this.buffer;
		this.buffer = [];
		this.bufferBytes = 0;
		const batch: OutgoingBatch = {
			sdk_version: this.config.sdkVersion,
			write_key: this.config.writeKey,
			anon_id: this.identity.anonId,
			session_id: this.identity.sessionId,
			embtrk: this.identity.embtrk,
			external_user_id: this.identity.externalUserId,
			email_sha256: this.identity.emailSha256,
			click_handle: this.identity.clickHandle,
			contact_ref: this.identity.contactRef,
			device_pixel_ratio: devicePixelRatio(),
			events,
		};
		const payload = JSON.stringify(batch);

		if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
			try {
				const blob = new Blob([payload], { type: 'application/json' });
				if (navigator.sendBeacon(this.config.endpoint, blob)) {
					log('queue: sent', events.length, 'via beacon');
					return Promise.resolve();
				}
			} catch (e) {
				log('queue: sendBeacon threw', e);
			}
		}

		if (typeof navigator !== 'undefined' && (navigator as any).onLine === false) {
			log('queue: offline, requeueing');
			this.requeue(events);
			return Promise.resolve();
		}

		return fetch(this.config.endpoint, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: payload,
			credentials: 'omit',
			keepalive: true,
			mode: 'cors',
		})
			.then((res) => {
				if (res.ok) {
					log('queue: sent', events.length, 'events');
					this.absorbConfig(res);
					return;
				}
				if (res.status >= 500) {
					log('queue: 5xx, requeueing');
					this.requeue(events);
				} else {
					// Any 403 means "stop sending": the edge answers 403 for both an
					// unknown and an inactive key (resolveWriteKey cannot tell them
					// apart), and for a disallowed origin. We deliberately don't read
					// the body to distinguish them — the response is the same signal.
					// The next flush() reads this back and mutes itself.
					if (res.status === 403) {
						log('queue: 403, muting this tab');
						storeConfig(this.config.writeKey, { level: this.config.level, active: false });
					}
					log('queue: 4xx, dropping batch', res.status);
				}
			})
			.catch((err) => {
				log('queue: fetch failed, requeueing', err);
				this.requeue(events);
			});
	}

	/** Caches the account config the worker reported, so the next page load in
	 *  this tab installs auto-tracking at the account's current level, and applies
	 *  any identity it resolved from a click handoff. Wrapped because the SDK runs
	 *  on someone else's page and must never throw. */
	private absorbConfig(res: Response): void {
		try {
			const parsed = parseConfigHeader(res.headers.get(CONFIG_HEADER_NAME));
			if (!parsed) return;
			storeConfig(this.config.writeKey, { level: parsed.level, active: true });
			if (parsed.level !== this.config.level) {
				log('queue: account level is', parsed.level, '(snippet says', this.config.level + ')');
			}
			if (this.onConfig) this.onConfig(parsed.level);

			// Only present on the response to the batch that carried a handle. The
			// edge resolved who this visitor is; from here on the SDK identifies them
			// by these hashes and stops sending the handle.
			if (parsed.identity && this.identity.absorbHandback(parsed.identity)) {
				log('queue: identity resolved from click handoff');
			}
		} catch (e) {
			log('queue: config header handling failed', e);
		}
	}

	private requeue(events: OutgoingEvent[]): void {
		this.buffer = events.concat(this.buffer);
		this.bufferBytes = this.buffer.reduce((acc, e) => acc + eventSize(e), 0);
	}

	/** Visible for tests only. */
	_state() {
		return {
			buffered: this.buffer.length,
			pending: this.pending.length,
			hasTimer: this.timer !== null,
		};
	}
}
